import { useCallback, useEffect } from "react";

import { Model } from "./useFile";
import { IFRAME_MESSAGES } from "../constants/IframeMessage";
import { IframeMessageData } from "../types/iframeMessage";
import {
  sortModelsByStatus,
  transformConsoleModelToAppModel,
  isWebpackOkMessage,
  isValidMessageData,
} from "../utils/iframeMessageUtils";
import { endpointStore } from "../utils/mobx/EndpointStore";

interface UseIframeListenerProps {
  /** 모델 목록을 설정하는 함수 */
  setModels: (models: Model[] | ((prev: Model[]) => Model[])) => void;
  /** iframe 준비 완료 상태를 설정하는 함수 */
  setIsIframeReady: (ready: boolean) => void;
}

/**
 * 부모 창으로부터 메시지를 수신하고 처리하는 훅
 *
 * @param setModels - 모델 목록 설정 함수
 * @param setIsIframeReady - iframe 준비 상태 설정 함수
 *
 * @example
 * ```tsx
 * const [models, setModels] = useState([]);
 * const [isIframeReady, setIsIframeReady] = useState(false);
 *
 * useIframeListener({
 *   setModels,
 *   setIsIframeReady,
 * });
 * ```
 */
export const useIframeListener = ({
  setModels,
  setIsIframeReady,
}: UseIframeListenerProps) => {
  const {
    setAccessToken,
    setSessionId,
    setTenantId,
    setUserSettings,
    setFeatureFlag,
  } = endpointStore;

  /**
   * webpackOk 이벤트 처리
   *
   * iframe 내부의 데모 앱이 새로고침되면, 부모 창은 이를 인지하지 못합니다.
   * 이 함수는 webpackOk 이벤트를 감지하여 부모 창에 "Request Message"를 보내
   * 필요한 데이터를 다시 요청하는 역할을 합니다.
   */
  const handleWebpackOk = useCallback(() => {
    window.parent.postMessage(IFRAME_MESSAGES.REQUEST_MESSAGE, "*");
  }, []);

  /**
   * 사용자 데이터 처리
   */
  const handleUserData = useCallback(
    (data: IframeMessageData) => {
      if (data.accessToken) {
        setAccessToken(data.accessToken);
      }
      if (data.sessionId) {
        setSessionId(data.sessionId);
      }
      if (data.tenantId) {
        setTenantId(data.tenantId);
      }
      if (data.userSettings) {
        setUserSettings(data.userSettings);
      }
      if (data.featureFlag) {
        setFeatureFlag(data.featureFlag);
      }
    },
    [
      setAccessToken,
      setSessionId,
      setTenantId,
      setUserSettings,
      setFeatureFlag,
    ],
  );

  /**
   * 접근 가능한 모델 정보 처리
   */
  const handleAccessibleModel = useCallback(
    (accessibleModel: IframeMessageData["accessibleModel"]) => {
      if (!accessibleModel || accessibleModel.length === 0) return;

      setModels([]);

      const sortedModels = sortModelsByStatus(accessibleModel);

      if (sortedModels.length > 0) {
        setIsIframeReady(true);
        sortedModels.forEach(modelFromConsole => {
          const appModel = transformConsoleModelToAppModel(modelFromConsole);
          setModels(prev => [...prev, appModel]);
        });
      }
    },
    [setModels, setIsIframeReady],
  );

  /**
   * 메시지 이벤트 핸들러
   */
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // webpackOk 메시지 처리
      if (isWebpackOkMessage(event.data)) {
        handleWebpackOk();
        return;
      }

      // 유효한 메시지 데이터인지 확인
      if (!isValidMessageData(event.data)) return;

      const data = event.data as IframeMessageData;

      // 사용자 데이터 처리
      handleUserData(data);

      // 접근 가능한 모델 처리
      if (data.accessibleModel) {
        handleAccessibleModel(data.accessibleModel);
      }
    },
    [handleWebpackOk, handleUserData, handleAccessibleModel],
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleMessage]);

  return { handleMessage };
};
